import { apiFetch } from './api-fetch.ts'

export type ComposioPreset = 'recommended' | 'read_only' | 'all' | 'custom'

export const composioPresets: ComposioPreset[] = [
  'recommended',
  'read_only',
  'all',
  'custom',
]

export interface ComposioAccount {
  id: string
  rootId: string
  ownerUserId: string
  composioConnectedAccountId: string | null
  toolkitSlug: string
  status: 'INITIATED' | 'ACTIVE' | 'FAILED' | 'REVOKED' | 'ARCHIVED'
  alias: string | null
  email: string | null
  displayName: string | null
  toolAccessPreset: ComposioPreset
  toolAllowlist: string[]
  createdAt: string
  updatedAt: string
}

export interface ComposioToolkit {
  slug: string
  name: string
  description?: string
  logo?: string
}

export interface ComposioClassifiedTool {
  slug: string
  name?: string
  description?: string
  readOnly: boolean
  recommended: boolean
  risk: 'read' | 'write' | 'dangerous'
}

// This client used to carry its own fetch whose fallback message was
// "<status> <statusText>"; it now shares `apiFetch`, so an error body's
// `error` still wins and the fallback names the path instead. The only
// other difference — the old copy had no 204 branch — cannot bite, as none
// of these endpoints reply empty.
export function getComposioStatus(): Promise<{
  configured: boolean
  ok?: boolean
  error?: string
}> {
  return apiFetch('/composio/status')
}

export function searchComposioToolkits(
  search: string,
): Promise<{ toolkits: ComposioToolkit[] }> {
  return apiFetch(`/composio/toolkits?search=${encodeURIComponent(search)}`)
}

export function listComposioTools(
  toolkit: string,
): Promise<{ tools: ComposioClassifiedTool[] }> {
  return apiFetch(`/composio/tools?toolkit=${encodeURIComponent(toolkit)}`)
}

export function listComposioAccounts(
  rootId: string,
  includeArchived = false,
): Promise<{ accounts: ComposioAccount[] }> {
  return apiFetch(
    `/composio/accounts?rootId=${rootId}` +
      (includeArchived ? '&includeArchived=1' : ''),
  )
}

export function activateComposioToolkit(body: {
  rootId: string
  toolkit: string
  presetId?: ComposioPreset
  allowedTools?: string[]
  callbackUrl?: string
  alias?: string | null
}): Promise<{ account: ComposioAccount; redirectUrl: string | null }> {
  return apiFetch('/composio/activate', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export function importComposioAccount(body: {
  rootId: string
  connectedAccountId: string
  toolkit?: string
  presetId?: ComposioPreset
  allowedTools?: string[]
  alias?: string | null
}): Promise<{ account: ComposioAccount }> {
  return apiFetch('/composio/import-existing', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export function updateComposioAccountAccess(
  id: string,
  body: {
    toolAccessPreset?: ComposioPreset
    toolAllowlist?: string[]
    alias?: string | null
  },
): Promise<{ account: ComposioAccount }> {
  return apiFetch(`/composio/accounts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })
}

export function archiveComposioAccount(
  id: string,
): Promise<{ account: ComposioAccount }> {
  return apiFetch(`/composio/accounts/${id}`, { method: 'DELETE' })
}

export function revokeComposioAccount(
  id: string,
  reason: string | null,
): Promise<{ account: ComposioAccount }> {
  return apiFetch(`/composio/accounts/${id}/revoke`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
}

export function syncComposioAccounts(
  rootId: string,
): Promise<{ accounts: ComposioAccount[] }> {
  return apiFetch('/composio/sync', {
    method: 'POST',
    body: JSON.stringify({ rootId }),
  })
}
